/**
 * Generating a firm's own precedent against a matter (VALE-809) —
 * `directApiClient`-backed, no server actions
 * (`lib/services/CLAUDE.md` § "Mutation pattern").
 */

import { directApiClient } from '@/lib/api/direct-client';
import type {
  EditPrecedentDocumentInWordResult,
  GeneratePrecedentDocumentInput,
  GeneratePrecedentDocumentResult,
  PrecedentDocumentOptions,
  PrecedentDocumentsResponse,
  RefreshPrecedentDocumentFromWordResult
} from './types';

const base = (matterId: number) => `/matters/${matterId}/precedent-documents`;

/** Every document generated from the firm's own precedents on this matter, newest first. */
export async function getPrecedentDocuments(
  matterId: number
): Promise<PrecedentDocumentsResponse> {
  return directApiClient.get<PrecedentDocumentsResponse>(base(matterId));
}

/**
 * What the generate dialog opens on.
 *
 * Markedly more expensive than the list above — it reads every candidate
 * precedent's bytes to discover the clause toggles it carries — which is why it
 * has a key of its own and is fetched only when the dialog opens.
 */
export async function getPrecedentDocumentOptions(
  matterId: number
): Promise<PrecedentDocumentOptions> {
  return directApiClient.get<PrecedentDocumentOptions>(
    `${base(matterId)}/options`
  );
}

/**
 * Generate one document.
 *
 * Regeneration is not a separate call: generating again from the same precedent
 * for the same addressee replaces that document and archives the files it
 * supersedes.
 */
export async function generatePrecedentDocument(
  matterId: number,
  input: GeneratePrecedentDocumentInput
): Promise<GeneratePrecedentDocumentResult> {
  return directApiClient.post<GeneratePrecedentDocumentResult>(
    `${base(matterId)}/generate`,
    input
  );
}

/**
 * Render an existing generated document again, against the matter as it stands
 * now. No body: every answer the render needs is already on the record.
 */
export async function regeneratePrecedentDocument(
  matterId: number,
  documentId: number
): Promise<GeneratePrecedentDocumentResult> {
  return directApiClient.post<GeneratePrecedentDocumentResult>(
    `${base(matterId)}/${documentId}/regenerate`,
    {}
  );
}

export async function editPrecedentDocumentInWord(
  matterId: number,
  documentId: number
): Promise<EditPrecedentDocumentInWordResult> {
  return directApiClient.post<EditPrecedentDocumentInWordResult>(
    `${base(matterId)}/${documentId}/edit-in-word`,
    {}
  );
}

export async function refreshPrecedentDocumentFromWord(
  matterId: number,
  documentId: number
): Promise<RefreshPrecedentDocumentFromWordResult> {
  return directApiClient.post<RefreshPrecedentDocumentFromWordResult>(
    `${base(matterId)}/${documentId}/refresh-from-word`,
    {}
  );
}
